import React, { useEffect, useState } from "react";
import "./LoginPage.css"; // Import the CSS file
import "./SignupPage.css";
import { useNavigate } from "react-router-dom";
import { useLogin } from "../../context/LoginProvider";
import { continueWithGoogle, signInWithGoogle, signup } from "../../utils/auth";
import AlertBox from "../../components/AlertBox/AlertBox";
import { GoogleLogin, useGoogleLogin } from "@react-oauth/google";
import { useSocket } from "../../context/SocketProvider";
import Navbar from "../../components/Navbar";
import Select from 'react-select';
import { countries } from "../../utils/countries";
import ReactCountryFlag from 'react-country-flag';

function SignupPage() {
  const { setUserData, setLoggedIn, isLoggedIn, guestData, setGuestUserData } =
    useLogin();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    age: "",
    country: null,
    relationshipType: "",
  });
  const [isDisabled, setDisabled] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    age: "",
  });
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [otpNotification, setOtpNotification] = useState("");

  useEffect(() => {
    if (isLoggedIn) navigate("/", { replace: true });
  }, [isLoggedIn]);

  // Clear OTP notification after 5 seconds
  useEffect(() => {
    if (otpNotification) {
      const timer = setTimeout(() => {
        setOtpNotification("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [otpNotification]);

  const acquireSession = (userData) => {
    return new Promise((resolve, reject) => {
      if (!socket) {
        reject(new Error("Socket not connected"));
        return;
      }

      socket.emit("acquireSession", userData, (response) => {
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response.user);
        }
      });
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "";
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "";

    const minLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*]/.test(password);

    if (!minLength) return "Password must be at least 8 characters long";
    if (!hasUpper) return "Password must contain at least one uppercase letter";
    if (!hasLower) return "Password must contain at least one lowercase letter";
    if (!hasNumber) return "Password must contain at least one number";
    if (!hasSpecial)
      return "Password must contain at least one special character (!@#$%^&*)";

    return "";
  };

  const validateAge = (age) => {
    if (!age) return "";
    const ageNum = parseInt(age);
    if (isNaN(ageNum) || ageNum < 13 || ageNum > 120) {
      return "Age must be between 13 and 120";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Validate on change
    if (name === "email") {
      const emailError = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    }
    if (name === "password") {
      const passwordError = validatePassword(value);
      setErrors((prev) => ({
        ...prev,
        password: passwordError,
      }));
    }
    if (name === "age") {
      const ageError = validateAge(value);
      setErrors((prev) => ({
        ...prev,
        age: ageError,
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const sendVerificationOTP = async (email) => {
    try {
      const response = await fetch(
        "https://soulmegle.com/api/auth/send-verification-otp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      setVerificationEmail(email);
      setIsOtpSent(true);
      setOtpNotification("OTP sent to your email");
    } catch (error) {
      setAlertMessage(error.message);
      setShowAlert(true);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDisabled(true);
    setAlertMessage("");

    // Validate inputs
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    const nameError = !formData.name.trim() ? "Name is required" : "";
    const ageError = validateAge(formData.age);
    const countryError = !formData.country ? "Country is required" : "";
    const relationshipError = !formData.relationshipType ? "Relationship type is required" : "";

    if (emailError || passwordError || nameError || ageError || countryError || relationshipError) {
      setAlertMessage(emailError || passwordError || nameError || ageError || countryError || relationshipError);
      setShowAlert(true);
      setDisabled(false);
      return;
    }

    if (!isOtpSent) {
      try {
        await sendVerificationOTP(formData.email);
        setDisabled(false);
        return;
      } catch (error) {
        setAlertMessage(error.message);
        setShowAlert(true);
        setDisabled(false);
        return;
      }
    }

    try {
      // Verify OTP
      const verifyResponse = await fetch(
        "https://soulmegle.com/api/auth/verify-email-otp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: verificationEmail,
            otp: otp,
          }),
        }
      );

      if (!verifyResponse.ok) {
        const error = await verifyResponse.text();
        throw new Error(error || "Invalid OTP");
      }

      // Proceed with registration
      const data = await signup(
        formData.name,
        formData.email,
        formData.password,
        formData.age,
        formData.country,
        formData.relationshipType,
        guestData.username
      );

      await acquireSession(data);
      setLoggedIn(true);
      setUserData({
        name: data.name,
        username: data.username,
        email: data.email,
        age: data.age,
        country: data.country,
        relationshipType: data.relationshipType,
        interests: data.interests,
      });

      navigate(data.interests.length === 0 ? "/cupid" : "/profile-summary", {
        replace: true,
      });
    } catch (error) {
      setAlertMessage(
        error.message || "Failed to create account. Please try again."
      );
      setShowAlert(true);
    } finally {
      setDisabled(false);
    }
  };

  async function handleGoogleLoginSuccess(tokenResponse) {
    setDisabled(true);
    const accessToken = tokenResponse.access_token;

    try {
      const userData = await continueWithGoogle(accessToken);
      if (userData.newUser) {
        // Redirect to complete profile if it's a new user
        setGuestUserData({
          name: userData.name,
          email: userData.email,
          googleId: userData.googleId,
          isGoogleUser: true,
        });
        navigate("/complete-profile", { replace: true });
      } else {
        // Existing user, login
        const data = await signInWithGoogle(accessToken);
        await acquireSession(data);
        setLoggedIn(true);
        setUserData({
          name: data.name,
          username: data.username,
          email: data.email,
          age: data.age,
          country: data.country,
          relationshipType: data.relationshipType,
          interests: data.interests,
        });
        navigate("/", { replace: true });
      }
    } catch (error) {
      setAlertMessage("Google login failed: " + error.message);
      setShowAlert(true);
    } finally {
      setDisabled(false);
    }
  }

  const loginWithGoogle = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: () => {
      setAlertMessage("Google login failed");
      setShowAlert(true);
    },
  });

  const closeAlert = () => {
    setShowAlert(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
      e.preventDefault();
      const form = e.target.closest("form");
      if (form) {
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton && !submitButton.disabled) {
          submitButton.click();
        }
      }
    }
  };

  const countryOptions = countries.map((country) => ({
    value: country.name,
    label: country.name,
    code: country.code,
  }));

  const relationshipOptions = [
    { value: "dating", label: "Dating" },
    { value: "friendship", label: "Friendship" },
    { value: "serious_relationship", label: "Serious Relationship" },
    { value: "casual", label: "Casual" },
    { value: "networking", label: "Networking" },
    { value: "any", label: "Any" },
  ];

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '48px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      padding: '0.25rem 0.5rem',
      boxShadow: 'none',
      fontFamily: 'Syne, sans-serif',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      borderColor: state.isFocused ? '#6616cd' : '#e5e7eb',
      '&:hover': {
        borderColor: '#6616cd'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0.25rem 0.5rem'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontFamily: 'Syne, sans-serif'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      fontFamily: 'Syne, sans-serif'
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '0.75rem 1rem',
      fontFamily: 'Syne, sans-serif',
      backgroundColor: state.isFocused ? '#f8f9ff' : 'white',
      color: state.isSelected ? 'white' : '#374151',
      '&:hover': {
        backgroundColor: state.isSelected ? '#6616cd' : '#f8f9ff'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#6b7280',
      '&:hover': {
        color: '#6616cd'
      }
    })
  };

  const formatOptionLabel = (option) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <ReactCountryFlag countryCode={option.code} svg />
      <span>{option.label}</span>
    </div>
  );

  const handleCountryChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, country: selectedOption ? selectedOption.value : null }));
  };

  const handleRelationshipTypeChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, relationshipType: selectedOption ? selectedOption.value : "" }));
  };

  return (
    <>
      <Navbar isHomePage={false} />
      <div className="login-page">
        <div className="signup-image">
          <div className="geometric-shapes">
            <div className="geometric-shape shape-1"></div>
            <div className="geometric-shape shape-2"></div>
            <div className="geometric-shape shape-3"></div>
            <div className="geometric-shape shape-4"></div>
            <div className="geometric-shape shape-5"></div>
          </div>
          <div className="signup-info">
            <h2>Welcome to Soulmegle</h2>
            <p>Join thousands of people finding meaningful connections through our AI-powered matching platform.</p>
            <ul className="signup-features">
              <li>
                <div className="feature-icon">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.75 2a.75.75 0 01.75.75v1.5h10.5a.75.75 0 010 1.5H4.5v12a.75.75 0 01-1.5 0V5.75H1.5a.75.75 0 010-1.5h1.5V2.75A.75.75 0 013.75 2z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span>AI-powered smart matching</span>
              </li>
              <li>
                <div className="feature-icon">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.66a.75.75 0 00-1.06 1.061l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span>Verified profiles & secure chats</span>
              </li>
              <li>
                <div className="feature-icon">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span>Audio & video conversations</span>
              </li>
              <li>
                <div className="feature-icon">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span>Global community worldwide</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="login-form">
          <div className="signup-container">
            <div className="login-header">
              <div className="brand-accent"></div>
              <h1>Create Account</h1>
              <p>Join the journey to find your soulmate</p>
            </div>

            {otpNotification && (
              <div className="otp-notification">
                <svg className="notification-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                </svg>
                {otpNotification}
              </div>
            )}

            <form onSubmit={handleSubmit} className="signup-form-content">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="signup-name">
                    <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                    </svg>
                    Full Name
                  </label>
                  <div className="input-container">
                    <input
                      id="signup-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="signup-age">
                    <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                    </svg>
                    Age
                  </label>
                  <div className="input-container">
                    <input
                      id="signup-age"
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      placeholder="Your age"
                      min="13"
                      max="120"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="signup-email">
                  <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                  </svg>
                  Email Address
                </label>
                <div className="input-container">
                  <input
                    id="signup-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="signup-password">
                  <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                  </svg>
                  Password
                </label>
                <div className="input-container">
                  <input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Create a strong password"
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={togglePasswordVisibility}
                  >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      {showPassword ? (
                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd"/>
                      ) : (
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                      )}
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                    </svg>
                  </button>
                </div>
                {errors.password && <div className="error-message">{errors.password}</div>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="signup-country">
                    <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd"/>
                    </svg>
                    Country
                  </label>
                  <div className="select-container">
                    <Select
                      id="signup-country"
                      value={countryOptions.find(option => option.value === formData.country)}
                      onChange={handleCountryChange}
                      options={countryOptions}
                      placeholder="Select your country"
                      isSearchable
                      styles={customStyles}
                      formatOptionLabel={formatOptionLabel}
                      menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="signup-relationship">
                    <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.75 2a.75.75 0 01.75.75v1.5h10.5a.75.75 0 010 1.5H4.5v12a.75.75 0 01-1.5 0V5.75H1.5a.75.75 0 010-1.5h1.5V2.75A.75.75 0 013.75 2z" clipRule="evenodd"/>
                      <path d="M9.5 12.75a2.75 2.75 0 115.5 0 2.75 2.75 0 01-5.5 0z"/>
                    </svg>
                    Looking For
                  </label>
                  <div className="select-container">
                    <Select
                      id="signup-relationship"
                      value={relationshipOptions.find(option => option.value === formData.relationshipType)}
                      onChange={handleRelationshipTypeChange}
                      options={relationshipOptions}
                      placeholder="What are you looking for?"
                      isSearchable={false}
                      styles={customStyles}
                      menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                    />
                  </div>
                </div>
              </div>

              {isOtpSent && (
                <div className="form-group">
                  <label className="form-label" htmlFor="signup-otp">
                    <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd"/>
                    </svg>
                    Verification Code
                  </label>
                  <div className="input-container">
                    <input
                      id="signup-otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter 6-digit code from your email"
                      maxLength="6"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="submit-container">
                <button type="submit" disabled={isDisabled} className="signup-btn">
                  {isDisabled ? (
                    <>
                      <div className="loading-spinner"></div>
                      {isOtpSent ? "Verifying..." : "Sending OTP..."}
                    </>
                  ) : (
                    <>
                      <svg className="btn-icon" fill="currentColor" viewBox="0 0 20 20">
                        {isOtpSent ? (
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        ) : (
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                        )}
                      </svg>
                      {isOtpSent ? "Verify & Create Account" : "Send Verification Code"}
                    </>
                  )}
                </button>
              </div>

              <div className="signup-link">
                <span>Already have an account?</span>
                <button type="button" onClick={() => navigate("/login", { replace: true })}>
                  Sign In
                </button>
              </div>
            </form>

            <div className="divider">
              <div className="divider-line"></div>
              <span className="divider-text">Or continue with</span>
              <div className="divider-line"></div>
            </div>

            <div className="social-buttons">
              <button className="google-button" onClick={loginWithGoogle}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.3055 10.5415H21.5V10.5H12.5V14.5H18.1515C17.327 16.8285 15.1115 18.5 12.5 18.5C9.1865 18.5 6.5 15.8135 6.5 12.5C6.5 9.1865 9.1865 6.5 12.5 6.5C14.0295 6.5 15.421 7.077 16.4805 8.0195L19.309 5.191C17.523 3.5265 15.134 2.5 12.5 2.5C6.9775 2.5 2.5 6.9775 2.5 12.5C2.5 18.0225 6.9775 22.5 12.5 22.5C18.0225 22.5 22.5 18.0225 22.5 12.5C22.5 11.8295 22.431 11.175 22.3055 10.5415Z"
                    fill="#FFC107"
                  />
                  <path
                    d="M3.65308 7.8455L6.93858 10.255C7.82758 8.054 9.98058 6.5 12.5001 6.5C14.0296 6.5 15.4211 7.077 16.4806 8.0195L19.3091 5.191C17.5231 3.5265 15.1341 2.5 12.5001 2.5C8.65908 2.5 5.32808 4.6685 3.65308 7.8455Z"
                    fill="#FF3D00"
                  />
                  <path
                    d="M12.4999 22.4999C15.0829 22.4999 17.4299 21.5114 19.2044 19.9039L16.1094 17.2849C15.0717 18.074 13.8036 18.5009 12.4999 18.4999C9.89891 18.4999 7.69041 16.8414 6.85841 14.5269L3.59741 17.0394C5.25241 20.2779 8.61341 22.4999 12.4999 22.4999Z"
                    fill="#4CAF50"
                  />
                  <path
                    d="M22.3055 10.5415H21.5V10.5H12.5V14.5H18.1515C17.7571 15.6082 17.0467 16.5766 16.108 17.2855L16.1095 17.2845L19.2045 19.9035C18.9855 20.1025 22.5 17.5 22.5 12.5C22.5 11.8295 22.431 11.175 22.3055 10.5415Z"
                    fill="#1976D2"
                  />
                </svg>
                Google
              </button>
            </div>
          </div>
        </div>

        {showAlert && (
          <AlertBox
            message={alertMessage}
            onClose={closeAlert}
            success="failure"
          />
        )}
      </div>
    </>
  );
}

export default SignupPage;
